<template>
    <div
        class="mx-auto w-full max-w-[852px] rounded-md bg-white shadow-card lg:mx-0 lg:ml-16"
    >
        <div class="mx-6 mb-[25px] mt-[18px]">
            <h4 class="font-medium leading-normal text-gray-600">
                {{ $t("vue.subscription_details") }}
            </h4>
            <p class="text-sm leading-normal text-gray-500">
                {{ $t("vue.subscription_breakdown") }}
            </p>
            <div class="">
                <!-- Left column -->
                <div
                    class="mt-3 rounded-md border border-gray-200 bg-gray-50 px-6"
                >
                    <div
                        class="flex flex-col justify-between md:flex-row lg:flex-col xl:flex-row"
                    >
                        <div class="pb-7 pt-6">
                            <div
                                class="inline-flex rounded-md bg-purple-300 px-3 py-[3px] text-xs font-medium text-gray-700"
                            >
                                {{
                                    subscription.period === "month"
                                        ? $t("vue.month_to_month")
                                        : $t("vue.annual_plan")
                                }}
                            </div>

                            <div
                                class="mt-5 text-2xl leading-normal tracking-tighter text-gray-800"
                            >
                                <span class="font-bold">
                                    ${{
                                        (
                                            subscription.items[0].unitPrice
                                                .amount *
                                            subscription.items[0].quantity
                                        ).toFixed(2)
                                    }}
                                </span>
                                <span>/{{ subscription.period }}</span>
                            </div>

                            <div class="text-sm text-gray-500">
                                for
                                {{ subscription.items[0].quantity }}
                                {{ $t("vue.domains") }}<br />
                            </div>

                            <div
                                v-if="subscription.canceled_at"
                                class="mt-[18px] text-sm text-gray-500"
                            >
                                <div>
                                    {{
                                        $t("vue.your_subscription_is_canceled")
                                    }}
                                </div>
                                <div class="font-medium text-gray-800">
                                    {{
                                        format(
                                            new Date(
                                                subscription.canceled_at.date,
                                            ),
                                            "d MMMM, yyyy",
                                        )
                                    }}
                                </div>
                            </div>

                            <div class="mt-8">
                                <div
                                    class="mt-[3px] cursor-pointer text-sm font-medium leading-normal text-custom-blue-500 hover:text-custom-blue-600"
                                    @click="handleChangeSubscription"
                                >
                                    {{ $t("vue.change_your_subscription") }}
                                </div>
                            </div>
                        </div>

                        <!-- Right column -->
                        <div class="pt-6 lg:min-w-[200px]">
                            <div
                                v-if="
                                    subscription.next_billing_at &&
                                    !subscription.canceled_at
                                "
                            >
                                <div
                                    class="inline-flex rounded-md bg-indigo-300 px-3 py-[3px] text-xs font-medium text-gray-700"
                                >
                                    {{ $t("vue.next_invoice_label") }}
                                </div>

                                <div
                                    class="mt-5 text-2xl leading-normal tracking-tighter text-gray-800"
                                >
                                    <span class="font-bold">
                                        {{
                                            format(
                                                new Date(
                                                    subscription.next_billing_at.date,
                                                ),
                                                "d MMMM, yyyy",
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>

                            <div
                                v-else-if="
                                    subscription.change &&
                                    subscription.change.action === 'cancel'
                                "
                            >
                                <span
                                    class="inline-flex rounded-md bg-yellow-300 px-3 py-[3px] text-xs font-medium text-gray-700"
                                >
                                    {{ $t("vue.cancelled_on") }}
                                    {{
                                        format(
                                            new Date(
                                                subscription.change.date.date,
                                            ),
                                            "d MMMM, yyyy",
                                        )
                                    }}
                                </span>
                            </div>
                        </div>

                        <div class="mt-6">
                            <a
                                :href="paddleCustomerPortalUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <AtomicButton
                                    class="w-full lg:w-auto"
                                    color="yellow"
                                    size="sm"
                                >
                                    {{
                                        $t(
                                            "vue.manage_subscription_invoices_lowercase",
                                        )
                                    }}
                                </AtomicButton>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { PropType } from "vue";
import type { IPaddleSubscription } from "@/types/Subscriptions/IPaddleSubscription";
import { format } from "date-fns";

defineProps({
    subscription: {
        type: Object as PropType<IPaddleSubscription | null>,
        required: true,
    },
    paddleCustomerPortalUrl: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["change-subscription"]);

const handleChangeSubscription = () => {
    emit("change-subscription");
};
</script>
